<template>
  <div
    :class="[
      $style.base,
      {
        [$style.isSelected]: isSelected,
        [$style.isAvailable]: isAvailable,
        [$style.isLoading]: isLoading,
        [$style.isTouch]: touch,
        [$style.isWaitingListEnabled]: waitinglistEnabled,
        [$style.hasWaitingList]: hasWaitingList,
        [$style.monthView]: monthView
      }
    ]"
    ref="container"
    @click="onClick"
  >
    <div :class="$style.inner" :style="innerStyle" v-test="'date-picker-inner'">
      <div :class="$style.content">
        <div :class="$style.weekday" v-if="!monthView">
          {{ isToday ? $t('general.today') : $d(parsedDate, 'weekday') }}
        </div>
        <div :class="$style.day">{{ $d(parsedDate, 'day') }}</div>
        <div :class="$style.month" v-if="!monthView">
          {{ $d(parsedDate, 'month') }}
        </div>
      </div>
      <IconClock
        v-if="hasWaitingList"
        colored
        :class="[$style.clock, { [$style.month]: monthView }]"
        v-test="'waiting-list-icon'"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Availability } from '@/types';
import { computed, onMounted, ref, watch } from 'vue';

import { storeToRefs } from 'pinia';

import IconClock from '@/components/IconClock.vue';
import { useAvailabilityStore } from '@/stores/availability';
import { useBookingStore } from '@/stores/booking';
import { useCompanyStore } from '@/stores/company';
import { device } from '@/user-context';
import widgetSettings from '@/widget-settings';
import dayjs from 'dayjs';
import { inject } from 'vue';

const props = defineProps<{
  data: Availability;
  monthView?: boolean;
  isLoading?: boolean;
}>();

const emit = defineEmits(['select', 'scrollToView']);

const { employeeId } = storeToRefs(useBookingStore());

const { touch } = device;

const container = ref<HTMLElement | null>(null);
const parsedDate = computed(() => new Date(`${availability.value.date}T00:00`));

const { date } = storeToRefs(useBookingStore());
const isToday = computed(() =>
  dayjs().isSame(dayjs(availability.value.date), 'day')
);
const isSelected = computed(() =>
  dayjs(availability.value.date).isSame(dayjs(date.value), 'day')
);

const { getAvailability } = useAvailabilityStore();

const availability = computed(
  (): Availability => getAvailability(props.data.date)
);

const innerStyle = computed(() => {
  if (!isSelected.value) {
    return {};
  }

  if (isAvailable.value) {
    return {
      background: widgetSettings.color,
      'border-color': widgetSettings.color
    };
  }
  return {
    background: widgetSettings.color
  };
});

const isAvailable = computed(() => {
  if (
    !availability.value.timeslots ||
    availability.value.timeslots.length === 0
  ) {
    return false;
  }

  if (!employeeId.value) {
    return true;
  }

  return (
    availability.value.timeslots.filter((slot) => {
      return slot.resource_ids.includes(employeeId.value as number);
    }).length > 0
  );
});

onMounted(() => {
  if (isSelected.value) {
    selectDate();
  }
});

const { waitinglistEnabled } = useCompanyStore();

const hasWaitingList = computed(() => {
  if (!waitinglistEnabled || !availability.value.open || isAvailable.value) {
    return false;
  }
  if (
    !availability.value.open_resources ||
    !availability.value.open_resources.length
  ) {
    return false;
  }
  if (
    employeeId.value &&
    !availability.value.open_resources.includes(employeeId.value)
  ) {
    return false;
  }

  return true;
});

const selectDate = () => {
  if (container.value) {
    emit(
      'select',
      container.value.offsetLeft + container.value.offsetWidth / 2,
      props.data
    );
  }
};

const gtag = inject<any>('gtag');

const onClick = () => {
  selectDate();
  gtag.trackEvent('date_select', {
    weekday: dayjs(availability.value.date).day()
  });
};

watch(
  date,
  () => {
    if (date.value === availability.value.date && container.value) {
      emit(
        'scrollToView',
        container.value.offsetLeft + container.value.offsetWidth / 2
      );
    }
  },
  { immediate: true }
);
</script>

<style lang="scss" module>
$itemCount: 7;

.base {
  position: relative;
  padding: 0 3px;
  flex-shrink: 0;

  &.isTouch:not(.monthView) {
    width: calc(14.5vw);

    &:first-child {
      margin-left: -3px;
    }
  }
  &.monthView {
    width: 100%;
    padding: 0 4px;
  }

  &:not(.isTouch):not(.monthView) {
    width: calc(
      calc(100vw / $itemCount) - calc(($slider-btn-size * 2) / $itemCount)
    );
  }

  &.isAvailable,
  &.hasWaitingList {
    cursor: pointer;
  }

  &:not(.isAvailable):not(.hasWaitingList) {
    pointer-events: none;
  }
}

@keyframes placeHolderShimmer {
  0% {
    background-position: 0px 0;
  }
  100% {
    background-position: 100em 0;
  }
}
.inner {
  position: relative;
  height: 70px;
  border-radius: $border-radius;
  border: 1px solid $color-border;

  .base.isWaitingListEnabled & {
    height: 85px;
    padding-bottom: 15px;
  }
  .base.monthView & {
    padding-bottom: 10%;
  }

  .base.isAvailable & {
    background-color: white;
  }

  .base.monthView & {
    height: 42px;
    margin-bottom: 8px;
  }

  .base:not(.isSelected):not(.isAvailable) & {
    &:before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
      background-image: linear-gradient(
        135deg,
        #e5e5e5 16.67%,
        transparent 16.67%,
        transparent 50%,
        #e5e5e5 50%,
        #e5e5e5 66.67%,
        transparent 66.67%,
        transparent 100%
      );
      background-size: 4.24px 4.24px;
    }
  }
  .base.isLoading & {
    animation-duration: 10s;
    animation-fill-mode: forwards;
    animation-iteration-count: infinite;
    animation-name: placeHolderShimmer;
    animation-timing-function: linear;
    background: fff;
    background: linear-gradient(to right, #e5e5e5 0%, #f5f5f5 30%, #e5e5e5 60%);
    backface-visibility: hidden;
    -webkit-backface-visibility: hidden;
    transition: background 0.5 ease;
  }
}

.clock {
  position: absolute;
  left: calc(50% - 15px);
  bottom: -15px;
  background-color: white;
  width: 30px;
  height: 30px;
  padding: 5px;
  border-radius: 15px;
  border: 1px solid #efefef;
  z-index: 1;

  &.month {
    left: calc(100% - 18px);
    bottom: -7px;
    width: 24px;
    height: 24px;
    padding: 3px;
  }
}

.loader {
  position: absolute;
  top: calc(100% - 12px);
  width: 24px;
  height: 24px;
}

.content {
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  height: 100%;
  padding: 5px 0;
  text-align: center;

  .base.isSelected & {
    color: $color-font-light;
  }
}

.weekday,
.month {
  font-size: 10px;
}

.weekday {
  text-transform: capitalize;
}

.month {
  text-transform: uppercase;
  @include font-sub;
}

.day {
  font-size: 24px;
  font-weight: bold;
}

.weekday,
.day {
  .base:not(.isAvailable):not(.isSelected) & {
    @include font-sub;
  }
}
</style>
